import React, { useState, useEffect, useContext } from 'react';
import AuthContext from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, Paper, CircularProgress, Alert,
  ToggleButton, ToggleButtonGroup, Divider,
} from '@mui/material';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import HotelIcon from '@mui/icons-material/Hotel';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import LocalCafeIcon from '@mui/icons-material/LocalCafe';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';

import { NAV_BG, AMBER, AMBER_DARK } from '../theme';
import { elUpperCase } from '../utils/text';
const GREEN = '#10b981';
const PURPLE = '#8b5cf6';
const GRAY = '#94a3b8';
const RED = '#ef4444';

function StatRow({ icon, label, value, sub, color }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75 }}>
      <Box sx={{ color: color || GRAY, display: 'flex', alignItems: 'center' }}>{icon}</Box>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.88rem', color: '#334155' }}>
          {label}
        </Typography>
        {sub && (
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.75rem', color: GRAY }}>
            {sub}
          </Typography>
        )}
      </Box>
      <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '0.95rem', color: NAV_BG }}>
        {value}
      </Typography>
    </Box>
  );
}

export default function ParentStatsPage() {
  const { authTokens, backendUrl } = useContext(AuthContext);
  const { t } = useTranslation();

  const [period, setPeriod] = useState('week');
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const authHeader = { 'Content-Type': 'application/json', Authorization: 'Bearer ' + authTokens?.access };

  useEffect(() => {
    setLoading(true);
    setError('');
    fetch(backendUrl + `api/parent/stats/?period=${period}`, { headers: authHeader })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => { setStats(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => { setError(t('parent_stats.load_error')); setLoading(false); });
  }, [period]);

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', p: 3 }}>
      <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 800, fontSize: '1.4rem', color: NAV_BG, mb: 2 }}>
        {t('parent_stats.title')}
      </Typography>

      <ToggleButtonGroup
        value={period}
        exclusive
        onChange={(_, v) => v && setPeriod(v)}
        size="small"
        sx={{ mb: 3 }}
      >
        <ToggleButton value="week" sx={{ fontFamily: '"Outfit", sans-serif', textTransform: 'none', px: 2 }}>
          {t('parent_stats.week')}
        </ToggleButton>
        <ToggleButton value="month" sx={{ fontFamily: '"Outfit", sans-serif', textTransform: 'none', px: 2 }}>
          {t('parent_stats.month')}
        </ToggleButton>
        <ToggleButton value="year" sx={{ fontFamily: '"Outfit", sans-serif', textTransform: 'none', px: 2 }}>
          {t('parent_stats.year')}
        </ToggleButton>
      </ToggleButtonGroup>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress sx={{ color: AMBER }} />
        </Box>
      )}

      {!loading && error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}

      {!loading && !error && stats.length === 0 && (
        <Alert severity="info" sx={{ borderRadius: 2 }}>{t('parent_stats.no_data')}</Alert>
      )}

      {!loading && !error && stats.map((child) => (
        <Paper key={child.child_id} sx={{ mb: 2.5, borderRadius: 2, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          {/* Child header */}
          <Box sx={{ px: 2.5, py: 1.75, background: NAV_BG, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: '50%',
              background: `linear-gradient(135deg, ${AMBER} 0%, #D97706 100%)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: NAV_BG, fontSize: '0.85rem',
            }}>
              {child.firstname?.[0]}{child.lastname?.[0]}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: 'white', fontSize: '0.95rem' }}>
                {child.firstname} {child.lastname}
              </Typography>
              <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.78rem', color: 'rgba(255,255,255,0.6)' }}>
                {child.period_label}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)' }}>
                {t('parent_stats.days_logged')}
              </Typography>
              <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: AMBER, fontSize: '1.1rem' }}>
                {child.days_with_data}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ px: 2.5, py: 2 }}>
            {/* Food */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <RestaurantIcon sx={{ fontSize: 15, color: GRAY }} />
              <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', color: GRAY, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                {elUpperCase(t('parent_stats.food'))}
              </Typography>
            </Box>
            <StatRow
              icon={<WbSunnyIcon sx={{ fontSize: 18 }} />}
              label={t('parent_stats.breakfast')}
              value={`${child.breakfast.days_eaten} ${t('parent_stats.days')}`}
              sub={child.breakfast.avg_servings > 0 ? `${t('parent_stats.avg')} ${child.breakfast.avg_servings} ${t('parent_stats.per_day')}` : undefined}
              color={AMBER}
            />
            <StatRow
              icon={<RestaurantIcon sx={{ fontSize: 18 }} />}
              label={t('parent_stats.lunch')}
              value={`${child.lunch.days_eaten} ${t('parent_stats.days')}`}
              sub={child.lunch.avg_servings > 0 ? `${t('parent_stats.avg')} ${child.lunch.avg_servings} ${t('parent_stats.per_day')}` : undefined}
              color={GREEN}
            />
            <StatRow
              icon={<LocalCafeIcon sx={{ fontSize: 18 }} />}
              label={t('parent_stats.afternoon')}
              value={`${child.afternoon.days_eaten} ${t('parent_stats.days')}`}
              sub={child.afternoon.avg_servings > 0 ? `${t('parent_stats.avg')} ${child.afternoon.avg_servings} ${t('parent_stats.per_day')}` : undefined}
              color='#3b82f6'
            />

            <Divider sx={{ my: 1.5 }} />

            {/* Sleep */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <HotelIcon sx={{ fontSize: 15, color: GRAY }} />
              <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', color: GRAY, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                {elUpperCase(t('parent_stats.sleep'))}
              </Typography>
            </Box>
            <StatRow
              icon={<HotelIcon sx={{ fontSize: 18 }} />}
              label={t('parent_stats.naps')}
              value={`${child.sleep.days_slept} ${t('parent_stats.days')}`}
              sub={child.days_with_data > 0 ? `${child.sleep.rate_pct}%` : undefined}
              color={PURPLE}
            />

            {(child.absences.full_days > 0 || child.absences.bus_only_days > 0) && (
              <>
                <Divider sx={{ my: 1.5 }} />

                {/* Absences */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <EventBusyIcon sx={{ fontSize: 15, color: GRAY }} />
                  <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', color: GRAY, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                    {elUpperCase(t('parent_stats.absences'))}
                  </Typography>
                </Box>
                {child.absences.full_days > 0 && (
                  <StatRow
                    icon={<EventBusyIcon sx={{ fontSize: 18 }} />}
                    label={t('parent_stats.full_absences')}
                    value={`${child.absences.full_days} ${t('parent_stats.days')}`}
                    color={RED}
                  />
                )}
                {child.absences.bus_only_days > 0 && (
                  <StatRow
                    icon={<DirectionsBusIcon sx={{ fontSize: 18 }} />}
                    label={t('parent_stats.bus_only_absences')}
                    value={`${child.absences.bus_only_days} ${t('parent_stats.days')}`}
                    color={AMBER_DARK}
                  />
                )}
              </>
            )}
          </Box>
        </Paper>
      ))}
    </Box>
  );
}
